import { ChangeEvent, useMemo } from "react";
import { TableWidgetRow } from "@hatch-digital-nl/abs-storybook/legacy";
import { useGetEmployeeScheduledQualificationsQuery } from "../../../common/api/scheduledQualification";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { useEmployeeId } from "../../../common/utils/useEmployeeId";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
    setScheduledQualificationsCurrentPage,
    setScheduledQualificationsItemsPerPage,
} from "../DashboardSlice";
import { mapScheduledQualificationsToRows } from "../utils/mapScheduledQualificationsToRows";
import { ScheduledQualificationsSortEnum } from "../../scheduledQualificationsPage/enums/ScheduledQualificationsSortEnum";

type UseDashboardScheduledQualificationsResult = {
    loading: boolean;
    table: {
        columns: string[];
        rows: TableWidgetRow[];
        total: number;
    };
    currentPage: number;
    itemsPerPage: number;
    itemsPerPageSelect: { value: number; activeValue: boolean }[];
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (event: ChangeEvent<HTMLSelectElement>) => void;
    title: string;
    isEmpty: boolean;
};

export const useDashboardScheduledQualifications =
    (): UseDashboardScheduledQualificationsResult => {
        const dispatch = useAppDispatch();
        const {
            scheduledQualifications: {
                currentPage,
                itemsPerPage,
                itemsPerPageSelect,
            },
        } = useAppSelector((state) => state.dashboard);

        const establishmentId = useEstablishmentId();
        const employeeId = useEmployeeId();

        const { data, isLoading, isFetching } =
            useGetEmployeeScheduledQualificationsQuery({
                filter: {
                    establishment_id: establishmentId,
                    employee_id: employeeId,
                },
                page: currentPage,
                items_per_page: itemsPerPage,
                sort: ScheduledQualificationsSortEnum.STARTS_AT_ASC,
            });

        const loading = isLoading || isFetching;

        const table = useMemo(() => {
            return {
                columns: [
                    "Naam kwalificatie",
                    "Opleider",
                    "Ingepland op",
                ] as string[],
                rows: mapScheduledQualificationsToRows({
                    scheduledQualifications: data?.data ?? [],
                }),
                total: data?.meta?.total ?? 0,
            };
        }, [data]);

        const onPageChange = (page: number): void => {
            dispatch(setScheduledQualificationsCurrentPage(page));
        };

        const onItemsPerPageChange = (
            event: ChangeEvent<HTMLSelectElement>,
        ): void => {
            dispatch(
                setScheduledQualificationsItemsPerPage(
                    Number(event.target.value),
                ),
            );
        };

        const title = `Ingeplande opleidingen (${data?.meta?.total ?? 0})`;
        const isEmpty =
            !loading && !(data && data.data && data.data.length && data.meta);

        return {
            loading,
            table,
            currentPage,
            itemsPerPage,
            itemsPerPageSelect,
            onPageChange,
            onItemsPerPageChange,
            title,
            isEmpty,
        };
    };
